import { Bell, BellRing, CalendarClock, CalendarDays, CheckCheck, CheckCircle2, CircleDollarSign, FileClock, FileWarning, History, PackageSearch, Play, Settings2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { PageHeader, ResponsiveDetailSurface, ResponsiveFilters, StateView } from "../components";
import { dateTime, useApiData } from "../hooks";
import { notificationContent } from "../notification-content";
import { notifyNotificationsChanged } from "../notification-events";
import { notificationTarget } from "../notification-routing";
import type { Notification, NotificationPage, NotificationPreference, NotificationTemplateCode } from "../types";
import { disablePushNotifications, enablePushNotifications, pushStatus, type PushStatus } from "../push-notifications";

const categories: Array<{ code: NotificationTemplateCode; label: string; description: string; icon: typeof Bell }> = [
  { code: "appointment_assigned", label: "Novo agendamento", description: "Serviços atribuídos a você", icon: CalendarDays },
  { code: "appointment_rescheduled", label: "Reagendamentos", description: "Mudanças de data ou horário", icon: CalendarClock },
  { code: "appointment_cancelled", label: "Cancelamentos", description: "Serviços cancelados", icon: CalendarClock },
  { code: "appointment_reminder_day", label: "Lembrete de 24h", description: "Aviso no dia anterior", icon: CalendarClock },
  { code: "appointment_reminder_hour", label: "Lembrete de 1h", description: "Aviso próximo ao atendimento", icon: CalendarClock },
  { code: "appointment_late", label: "Serviço atrasado", description: "Serviço que ainda não iniciou", icon: CalendarClock },
  { code: "weekly_schedule_summary", label: "Resumo semanal", description: "Agenda enviada aos gestores no domingo", icon: CalendarDays },
  { code: "quote_accepted", label: "Orçamentos", description: "Orçamentos aceitos", icon: CheckCheck },
  { code: "payment_overdue", label: "Financeiro", description: "Cobranças vencidas e ainda abertas", icon: CircleDollarSign },
  { code: "work_order_started", label: "Serviço iniciado", description: "Início da execução em campo", icon: Play },
  { code: "work_order_completed", label: "Serviço finalizado", description: "Conclusão da execução em campo", icon: CheckCircle2 },
  { code: "inventory_below_minimum", label: "Estoque", description: "Materiais abaixo do mínimo definido", icon: PackageSearch },
  { code: "document_expiring", label: "Documentos", description: "Documentos próximos do vencimento", icon: FileClock },
];
const legacyWorkOrderCategory = { code: "work_order_document_pending" as const, label: "Ordens de serviço", description: "Alerta legado desativado", icon: FileWarning };
const states = [
  ["unread", "Novas"], ["active", "Ativas"], ["read", "Lidas"], ["resolved", "Histórico"], ["all", "Todas"],
] as const;

export function NotificationsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<(typeof states)[number][0]>("unread");
  const [templateCode, setTemplateCode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [draftPreferences, setDraftPreferences] = useState<NotificationPreference[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pushState, setPushState] = useState<PushStatus>("unsupported");
  const [pushBusy, setPushBusy] = useState(false);
  const query = useMemo(() => {
    const params = new URLSearchParams({ state, pageSize: "100" });
    if (templateCode) params.set("templateCode", templateCode);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return `/notifications?${params}`;
  }, [from, state, templateCode, to]);
  const page = useApiData<NotificationPage>(query);
  const preferences = useApiData<{ data: NotificationPreference[] }>(preferencesOpen ? "/notifications/preferences" : null);
  const counts = page.data?.counts ?? { all: 0, unread: 0, read: 0, resolved: 0 };

  const openPreferences = async () => {
    setPreferencesOpen(true); setError("");
    void pushStatus().then(setPushState).catch(() => setPushState("disabled"));
    const data = preferences.data?.data;
    if (data) setDraftPreferences(data);
  };
  useEffect(() => {
    if (preferencesOpen && preferences.data?.data.length) setDraftPreferences(preferences.data.data);
  }, [preferences.data, preferencesOpen]);

  const markReadAndOpen = async (notification: Notification) => {
    if (!notification.readAt && !notification.resolvedAt) {
      try { await api(`/notifications/${notification.id}/read`, { method: "POST" }); notifyNotificationsChanged(); await page.reload({ background: true }); }
      catch { /* A navegação continua; o usuário não perde o destino. */ }
    }
    navigate(notificationTarget(notification));
  };
  const markAll = async () => {
    setBusy(true); setError("");
    try { await api("/notifications/read-all", { method: "POST" }); notifyNotificationsChanged(); await page.reload(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível atualizar as notificações"); }
    finally { setBusy(false); }
  };
  const savePreferences = async () => {
    setBusy(true); setError("");
    try {
      await api("/notifications/preferences", { method: "PUT", body: JSON.stringify({ preferences: draftPreferences }) });
      setPreferencesOpen(false);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível salvar as preferências"); }
    finally { setBusy(false); }
  };
  const clearFilters = () => { setTemplateCode(""); setFrom(""); setTo(""); };
  const togglePush = async () => {
    setPushBusy(true); setError("");
    try {
      if (pushState === "subscribed") await disablePushNotifications();
      else await enablePushNotifications();
      setPushState(await pushStatus());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível alterar as notificações deste aparelho");
    } finally { setPushBusy(false); }
  };

  return <>
    <section className="page-intro"><div className="app-container"><PageHeader title="Notificações" /></div></section>
    <section className="app-section compact"><div className="app-container notifications-page">
      <div className="management-summary-grid notification-summary-grid">
        <article><span className="management-summary-icon is-blue"><Bell size={19} /></span><span>Novas<strong>{counts.unread}</strong></span></article>
        <article><span className="management-summary-icon is-amber"><CalendarClock size={19} /></span><span>Ativas<strong>{counts.unread + counts.read}</strong></span></article>
        <article><span className="management-summary-icon is-green"><CheckCheck size={19} /></span><span>Lidas<strong>{counts.read}</strong></span></article>
        <article><span className="management-summary-icon is-purple"><History size={19} /></span><span>Histórico<strong>{counts.resolved}</strong></span></article>
      </div>

      <div className="workspace-card notification-toolbar">
        <div className="notification-status-tabs" role="tablist">{states.map(([value, label]) => <button key={value} type="button" className={state === value ? "active" : ""} onClick={() => setState(value)}>{label}</button>)}</div>
        <div className="notification-toolbar-actions">
          {counts.unread > 0 && <button className="site-button site-button-outline site-button-small" disabled={busy} onClick={() => void markAll()}><CheckCheck size={16} />{busy ? "Atualizando..." : "Marcar todas como lidas"}</button>}
          <button className="site-button site-button-outline site-button-small" onClick={() => void openPreferences()}><Settings2 size={16} /> Preferências</button>
        </div>
        <ResponsiveFilters className="notification-filters" summary={[categories.find(({ code }) => code === templateCode)?.label, from && to ? `${from} — ${to}` : ""].filter(Boolean).join(" · ") || "Todos os tipos"} activeCount={Number(Boolean(templateCode)) + Number(Boolean(from || to))}>
          <div className="notification-filter-fields">
            <label>Categoria<select value={templateCode} onChange={(event) => setTemplateCode(event.target.value)}><option value="">Todas</option>{categories.map(({ code, label }) => <option key={code} value={code}>{label}</option>)}</select></label>
            <label>De<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
            <label>Até<input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
            <button type="button" className="site-button site-button-outline site-button-small" onClick={clearFilters}><X size={15} /> Limpar</button>
          </div>
        </ResponsiveFilters>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <StateView loading={page.loading} error={page.error} empty={(page.data?.data.length ?? 0) === 0}>
        <div className="workspace-card notification-history-list">{(page.data?.data ?? []).map((notification) => {
          const category = categories.find(({ code }) => code === notification.templateCode) ?? legacyWorkOrderCategory;
          const Icon = category.icon;
          const content = notificationContent(notification);
          return <button type="button" key={notification.id} className={!notification.readAt && !notification.resolvedAt ? "unread" : ""} onClick={() => void markReadAndOpen(notification)}>
            <span className="notification-history-icon"><Icon size={19} /></span>
            <span className="notification-history-copy"><span><strong>{content.title}</strong><small>{category.label}</small></span><p>{content.detail}</p><time>{dateTime(notification.createdAt)}</time></span>
            <span className={`notification-state${notification.resolvedAt ? " resolved" : notification.readAt ? " read" : ""}`}>{notification.resolvedAt ? "Resolvida" : notification.readAt ? "Lida" : "Nova"}</span>
          </button>;
        })}</div>
      </StateView>
    </div></section>
    {preferencesOpen && <ResponsiveDetailSurface title="Preferências de notificações" onClose={() => setPreferencesOpen(false)}>
      <section className="workspace-card notification-preferences">
        <header><div><span className="site-eyebrow">Alertas</span><h2>O que você quer acompanhar</h2></div><button type="button" className="icon-close" aria-label="Fechar" onClick={() => setPreferencesOpen(false)}><X size={18} /></button></header>
        <div className="notification-push-setting">
          <span className="notification-history-icon"><BellRing size={18} /></span>
          <span><strong>Notificações neste aparelho</strong><small>{pushState === "subscribed" ? "Ativas" : pushState === "denied" ? "Bloqueadas nas configurações do aparelho" : pushState === "disabled" ? "Indisponíveis no servidor" : pushState === "unsupported" ? "Não compatível com este navegador" : "Receba alertas mesmo com o app fechado"}</small></span>
          {(pushState === "prompt" || pushState === "subscribed") && <button type="button" className="site-button site-button-outline site-button-small" onClick={() => void togglePush()} disabled={pushBusy}>{pushBusy ? "Aguarde…" : pushState === "subscribed" ? "Desativar" : "Ativar"}</button>}
        </div>
        <StateView loading={preferences.loading} error={preferences.error}>{categories.map(({ code, label, description, icon: Icon }) => {
          const preference = draftPreferences.find((item) => item.templateCode === code);
          const enabled = preference?.enabled ?? true;
          const mandatory = preference?.mandatory ?? false;
          return <label className={`notification-preference${mandatory ? " is-mandatory" : ""}`} key={code}><span className="notification-history-icon"><Icon size={18} /></span><span><strong>{label}</strong><small>{mandatory ? "Obrigatório para gestores" : description}</small></span><input type="checkbox" checked={enabled} disabled={mandatory} onChange={(event) => setDraftPreferences((current) => {
            const next = current.filter((item) => item.templateCode !== code); return [...next, { templateCode: code, enabled: event.target.checked, mandatory }];
          })} /></label>;
        })}</StateView>
        <button className="site-button site-button-navy" disabled={busy || draftPreferences.length === 0} onClick={() => void savePreferences()}>{busy ? "Salvando..." : "Salvar preferências"}</button>
      </section>
    </ResponsiveDetailSurface>}
  </>;
}
